import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PackageCard } from "@/components/PackageCard";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Compass, Sparkle, ArrowLeft, ArrowRight, GraduationCap, ArrowCounterClockwise } from "@phosphor-icons/react";

const LEVELS = ["Pre-K", "Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5"];

const QUESTIONS = [
  {
    id: "age", q: "How old is your child?", emoji: "🎂",
    options: [
      { label: "3–4 years", level: 0 }, { label: "5 years", level: 1 }, { label: "6 years", level: 2 },
      { label: "7 years", level: 3 }, { label: "8 years", level: 4 }, { label: "9 years", level: 5 }, { label: "10+ years", level: 6 },
    ],
  },
  {
    id: "letters", q: "How well does your child know letter sounds?", emoji: "🔤",
    options: [
      { label: "Just starting out", score: 0 }, { label: "Knows most of them", score: 1 }, { label: "Knows them all", score: 2 },
    ],
  },
  {
    id: "sentences", q: "Can your child read simple sentences?", emoji: "📖",
    options: [
      { label: "Not yet", score: 0 }, { label: "With some help", score: 1 }, { label: "Yes, on their own", score: 2 },
    ],
  },
  {
    id: "story", q: "How does your child handle a short story?", emoji: "📚",
    options: [
      { label: "Not ready yet", score: 0 }, { label: "Reads it slowly", score: 1 }, { label: "Reads smoothly & understands", score: 2 },
    ],
  },
];

function computeLevel(answers) {
  const ageLevel = answers[0]?.level ?? 0;
  const skill = (answers[1]?.score ?? 0) + (answers[2]?.score ?? 0) + (answers[3]?.score ?? 0);
  let idx = ageLevel;
  if (skill <= 1) idx -= 1;
  else if (skill >= 5) idx += 1;
  idx = Math.max(0, Math.min(LEVELS.length - 1, idx));
  return LEVELS[idx];
}

export default function Quiz() {
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const isResult = step >= QUESTIONS.length;
  const recommended = isResult ? computeLevel(answers) : null;
  const matches = isResult ? packages.filter((p) => p.level === recommended) : [];

  useEffect(() => {
    if (isResult && recommended && user) {
      api.post("/quiz/result", { level: recommended }).then(() => checkAuth()).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResult, recommended, user?.user_id]);

  const choose = (opt) => {
    const next = [...answers];
    next[step] = opt;
    setAnswers(next);
    setStep(step + 1);
  };

  const restart = () => { setStep(0); setAnswers([]); };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="quiz-page">
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-sky/10 text-brand-sky font-semibold text-sm">
          <Compass size={16} weight="duotone" /> Find My Level
        </span>
        <h1 className="mt-4 font-heading font-bold text-4xl sm:text-5xl text-ink tracking-tight">Let's find the perfect start</h1>
        <p className="font-body text-ink-soft mt-3 text-lg">A few quick questions to match your child to the right reading level.</p>
      </div>

      {user?.quiz_level && step === 0 && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-brand-grass/10 border-2 border-brand-grass/20 rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="quiz-welcome-back">
          <span className="flex items-center gap-2 font-body text-ink">
            <Sparkle size={20} weight="fill" className="text-brand-grass" />
            Welcome back! Last time we recommended <span className="font-bold">{user.quiz_level}</span>.
          </span>
          <Link to="/catalog" className="sm:ml-auto">
            <Button size="sm" className="rounded-full bg-brand-grass hover:bg-brand-grass/90 text-white font-semibold" data-testid="quiz-welcome-browse">
              See those packages <ArrowRight size={16} weight="bold" className="ml-1" />
            </Button>
          </Link>
        </motion.div>
      )}

      {!isResult && (
        <>
          <div className="mb-6">
            <div className="flex justify-between font-body text-sm text-ink-soft mb-2">
              <span>Question {step + 1} of {QUESTIONS.length}</span>
              <span className="font-semibold">{Math.round((step / QUESTIONS.length) * 100)}%</span>
            </div>
            <Progress value={(step / QUESTIONS.length) * 100} className="h-2.5" />
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
              className="bg-white sticker rounded-3xl p-8">
              <div className="text-center mb-6">
                <span className="text-5xl">{QUESTIONS[step].emoji}</span>
                <h2 className="font-heading font-bold text-2xl text-ink mt-3">{QUESTIONS[step].q}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {QUESTIONS[step].options.map((opt, i) => (
                  <button key={i} onClick={() => choose(opt)}
                    className="text-left font-body font-semibold text-ink px-5 py-4 rounded-2xl border-2 border-slate-900/10 hover:border-brand-sky hover:bg-brand-sky/5 transition-colors"
                    data-testid={`quiz-opt-${step}-${i}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="mt-6 inline-flex items-center gap-2 font-body font-semibold text-ink-soft hover:text-brand-sky" data-testid="quiz-back">
                  <ArrowLeft size={18} weight="bold" /> Back
                </button>
              )}
            </motion.div>
          </AnimatePresence>
        </>
      )}

      {isResult && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="quiz-result">
          <div className="bg-white sticker rounded-3xl p-8 text-center mb-8">
            <div className="grid place-items-center w-16 h-16 mx-auto rounded-3xl bg-brand-grass/15 mb-4">
              <GraduationCap size={32} weight="duotone" className="text-brand-grass" />
            </div>
            <p className="font-body text-ink-soft">We recommend starting at</p>
            <h2 className="font-heading font-bold text-4xl text-ink tracking-tight mt-1" data-testid="quiz-recommended-level">{recommended}</h2>
            <p className="font-body text-ink-soft mt-3 max-w-lg mx-auto leading-relaxed">
              Based on your answers, these {recommended} packages are the perfect place to build confident reading skills.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <button onClick={restart} className="inline-flex items-center gap-2 font-body font-semibold text-ink-soft hover:text-brand-sky" data-testid="quiz-retake">
                <ArrowCounterClockwise size={18} weight="bold" /> Retake quiz
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {matches.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}
          </div>

          <div className="text-center mt-10">
            <Link to="/catalog">
              <Button variant="outline" className="rounded-full border-2 font-semibold px-8 py-6 text-lg" data-testid="quiz-browse-all">
                Browse all levels <ArrowRight size={18} weight="bold" className="ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
}
